import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldLogo } from "../components/landing/Logo";
import { Button } from "../components/ui/button";
import { Mail, ArrowLeft, MessageSquare, Phone } from "lucide-react";

const ECHO_PHONE = process.env.REACT_APP_ECHO_PHONE || "";

export default function TrialCheckout() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#040F49] flex flex-col">
      {/* Header */}
      <header className="border-b border-gold/10 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate("/trial/dashboard")}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <ShieldLogo size="sm" />
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-2xl w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gold/10 border border-gold/30 mb-6">
              <ShieldLogo size="md" />
            </div>

            <h1 className="font-inter text-3xl sm:text-4xl font-black text-white mb-4">
              Founding Restaurant Program
            </h1>
            <p className="text-base text-slate-300 leading-relaxed max-w-lg mx-auto">
              We are preparing the Founding Restaurant program — a limited invitation for real restaurant operators
              to help shape what Argus Xenia becomes.
            </p>
          </motion.div>

          {/* Status card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="bg-[#0A1244] border border-gold/20 rounded-xl p-8 mb-8"
          >
            <h2 className="font-inter text-lg font-bold text-gold mb-4">
              Program Details — Being Finalized
            </h2>

            <div className="space-y-3 text-sm text-slate-300">
              <p className="flex items-start gap-3">
                <span className="text-gold font-bold shrink-0 mt-0.5">→</span>
                <span>
                  The Founding Restaurant program will be available soon. Exact enrollment details,
                  program terms and pricing are being finalized by the Argus Xenia team.
                </span>
              </p>
              <p className="flex items-start gap-3">
                <span className="text-gold font-bold shrink-0 mt-0.5">→</span>
                <span>
                  Founding Restaurants receive access to all currently available V1 capabilities and
                  will have direct input into what gets built next.
                </span>
              </p>
              <p className="flex items-start gap-3">
                <span className="text-gold font-bold shrink-0 mt-0.5">→</span>
                <span>
                  Your trial data — restaurant profile, SOPs, Vision analyses — will carry over
                  seamlessly when you join. You will not start from scratch.
                </span>
              </p>
              <p className="flex items-start gap-3">
                <span className="text-gold font-bold shrink-0 mt-0.5">→</span>
                <span>
                  We will contact trial participants directly once enrollment is open.
                </span>
              </p>
            </div>
          </motion.div>

          {/* Contact options */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="grid sm:grid-cols-2 gap-4 mb-8"
          >
            <a
              href="mailto:andreas@argusxenia.com?subject=Founding Restaurant Program Interest"
              data-testid="founding-email-btn"
              className="flex items-center gap-4 p-5 bg-[#0A1244] border border-gold/20 rounded-xl hover:border-gold/50 transition-all group"
            >
              <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-gold" />
              </div>
              <div>
                <p className="text-sm font-bold text-white group-hover:text-gold transition-colors">Email Andreas</p>
                <p className="text-xs text-slate-400">andreas@argusxenia.com</p>
              </div>
            </a>

            <button
              onClick={() => navigate("/")}
              data-testid="founding-chat-btn"
              className="flex items-center gap-4 p-5 bg-[#0A1244] border border-lime/20 rounded-xl hover:border-lime/50 transition-all group text-left"
            >
              <div className="w-10 h-10 rounded-lg bg-lime/10 flex items-center justify-center shrink-0">
                <MessageSquare className="w-5 h-5 text-lime" />
              </div>
              <div>
                <p className="text-sm font-bold text-white group-hover:text-lime transition-colors">Talk to AI Argus</p>
                <p className="text-xs text-slate-400">Ask about the program</p>
              </div>
            </button>
          </motion.div>

          {ECHO_PHONE && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-center"
            >
              <p className="text-sm text-slate-500 mb-2">Or call Echo directly</p>
              <a
                href={`tel:${ECHO_PHONE}`}
                data-testid="founding-phone-link"
                className="inline-flex items-center gap-2 text-gold font-mono text-base hover:text-gold/80 transition-colors"
              >
                <Phone className="w-4 h-4" />
                {ECHO_PHONE}
              </a>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center mt-10"
          >
            <Button
              variant="outline"
              onClick={() => navigate("/trial/dashboard")}
              className="border-slate-600 text-slate-400 hover:text-white hover:border-slate-400"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return to Dashboard
            </Button>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
